import { Button } from '@/app/common/components/button/Button'
import { Input } from '@/app/common/components/input/Input'
import { SubmitButton } from '@/app/common/components/SubmitButton'
import { Checkbox } from '@/shad/ui/checkbox'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/shad/ui/form'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shad/ui/select'
import { Switch } from '@/shad/ui/switch'
import { Textarea } from '@/shad/ui/textarea'
import { zodResolver } from '@hookform/resolvers/zod'
import { MutationStatus } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import * as z from 'zod'
import { PaymentRulesConfig, TargetType, targetTypeSchema } from '../../schema'
import { removeUnderscoresAndMakeFirstLetterCapital } from '../../utils'

// Zod schema for form validation
const ruleFormSchema = z.object({
  name: z
    .string()
    .min(1, 'Rule name is required')
    .max(100, 'Rule name must be less than 100 characters'),
  description: z
    .string({
      message: 'Description is required',
    })
    .min(5, 'Description must be at least 5 characters'),
  targetType: targetTypeSchema,
  targetId: z.string().min(1, 'Please select a target'),
  approvalType: z.string().min(1, 'Please select an approval type'),
  is_active: z.boolean(),
})

export type RuleFormValues = z.infer<typeof ruleFormSchema>
interface CreateRuleFormProps {
  approvalRulesConfig: PaymentRulesConfig
  categories: { id: number; name: string }[]
  items: { id: number; name: string }[]
  onSubmit: (data: RuleFormValues) => void
  onCancel: () => void
  createRuleStatus: MutationStatus
  defualtValues?: RuleFormValues
}

function CreateRuleForm({
  categories,
  items,
  onSubmit,
  onCancel,
  createRuleStatus,
  defualtValues,
  approvalRulesConfig,
}: CreateRuleFormProps) {
  // React Hook Form setup
  const form = useForm<RuleFormValues>({
    resolver: zodResolver(ruleFormSchema),
    defaultValues: defualtValues ?? {
      approvalType: '',
      description: '',
      name: '',
      targetId: '',
      targetType: 'CATEGORY',
      is_active: true,
    },
  })

  const watchedTargetType = form.watch('targetType')

  const handleSubmit = (data: RuleFormValues) => {
    onSubmit(data)
  }

  const handleCancel = () => {
    onCancel()
    form.reset()
  }

  function getTargetSelection(targetType: TargetType) {
    switch (targetType) {
      case 'CATEGORY':
        return categories
      case 'ITEM':
        return items
      case 'PAYMENT_WORKFLOW':
        return approvalRulesConfig.global_payment_types
      default: {
        const _exhaustiveCheck: never = targetType
        _exhaustiveCheck // Satisfy linter by using the variable
        return []
      }
    }
  }

  function onError(error: unknown) {
    console.log(error)
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleSubmit, onError)}
        className="space-y-4"
      >
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Rule Name *</FormLabel>
              <FormControl>
                <Input
                  name={field.name}
                  placeholder="Enter rule name"
                  onChange={field.onChange}
                  value={field.value}
                  onBlur={field.onBlur}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Optional description"
                  rows={3}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="targetType"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Target Type *</FormLabel>
              <Select
                onValueChange={(value) => {
                  field.onChange(value)
                  form.setValue('targetId', '')
                }}
                value={field.value}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select target type" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {approvalRulesConfig.rule_target_types.map((option) => (
                    <SelectItem key={option.id} value={option.name}>
                      {removeUnderscoresAndMakeFirstLetterCapital(option.name)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        {watchedTargetType && (
          <FormField
            control={form.control}
            name="targetId"
            render={({ field }) => {
              return (
                <FormItem>
                  <FormLabel>Target Selection *</FormLabel>
                  <Select
                    onValueChange={(value) => {
                      field.onChange(value)
                    }}
                    value={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue
                          placeholder={`Select ${removeUnderscoresAndMakeFirstLetterCapital(
                            watchedTargetType
                          )}`}
                        />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {getTargetSelection(watchedTargetType).map((option) => (
                        <SelectItem
                          key={option.id}
                          value={option.id.toString()}
                        >
                          {option.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )
            }}
          />
        )}

        <FormField
          control={form.control}
          name="approvalType"
          render={() => (
            <FormItem>
              <FormLabel>Approval Type *</FormLabel>
              <div className="space-y-2">
                <FormField
                  control={form.control}
                  name="approvalType"
                  render={({ field }) => {
                    return (
                      <div className="space-y-2">
                        {approvalRulesConfig.approval_types.map((option) => (
                          <FormItem className="flex flex-row items-center space-x-3 space-y-0">
                            <FormControl>
                              <Checkbox
                                checked={field.value?.includes(option.name)}
                                onCheckedChange={() =>
                                  field.onChange(option.name)
                                }
                              />
                            </FormControl>
                            <FormLabel className="font-normal h-full block">
                              {removeUnderscoresAndMakeFirstLetterCapital(
                                option.name
                              )}
                            </FormLabel>
                          </FormItem>
                        ))}
                      </div>
                    )
                  }}
                />
              </div>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="is_active"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center space-x-3 space-y-0">
              <FormControl>
                <Switch
                  name={field.name}
                  checked={field.value}
                  onCheckedChange={(checked) => {
                    field.onChange(checked)
                    form.setValue('is_active', checked)
                  }}
                />
              </FormControl>
              <FormLabel>Active</FormLabel>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex justify-end space-x-2 pt-4">
          <Button type="button" variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <SubmitButton buttonTxt={'Save rule'} status={createRuleStatus} />
        </div>
      </form>
    </Form>
  )
}

export default CreateRuleForm
